<template>
  <div class="s-map">
    <div class="map-container" ref="mapContainer"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from 'vue';
import { loadGMap, getGlobalConfigs } from '@/utils/site-utils';
import { isMobile } from '@/utils/dom-utils';

const props = defineProps({
  // 经纬度坐标
  lat: {
    type: Number,
    required: true,
  },
  lng: {
    type: Number,
    required: true,
  },
  // 缩放级别
  zoom: {
    type: Number,
    default: 15,
  },
  // 地图高度
  height: {
    type: String,
    default: '200px',
  },
  // 标记图标
  markerIcon: {
    type: [String, Object],
    default: null,
  },
  // 标记标题
  markerTitle: {
    type: String,
    default: '',
  },
});

// Reactive data
const mapContainer = ref(null);
const map = ref(null);
const markers = ref([]);
const labels = ref([]);

// Get current instance for JSS
const { proxy } = getCurrentInstance();
const { $jss } = proxy;

// Initialize map
const initMap = async () => {
  try {
    const page = $jss.routeData();

    // 调试：检查gmapKey配置
    const [gmapKey] = getGlobalConfigs(page, 'gmapKey');

    const gmaps = await loadGMap(page);

    if (!gmaps) {
      throw new Error('Failed to load Google Maps - gmapKey not configured in Sitecore');
    }

    // 确保 Google Maps API 完全加载
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API not fully loaded');
    }

    const [gmapStyleId] = getGlobalConfigs(page, 'gmapStyleId');

    if (!mapContainer.value) {
      throw new Error('Map container not found');
    }

    // 等待 DOM 完全准备好
    await nextTick();

    // 调试：输出经纬度信息
    console.log('Map center coordinates:', { lat: props.lat, lng: props.lng });

    const mapOptions = {
      mapId: gmapStyleId?.value,
      center: { lat: props.lat, lng: props.lng }, // 经纬度作为地图中心点
      zoom: props.zoom,
      mapTypeId: gmaps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    };

    // 强制设置容器尺寸
    if (mapContainer.value.offsetWidth === 0 || mapContainer.value.offsetHeight === 0) {
      mapContainer.value.style.width = '100%';
      mapContainer.value.style.height = isMobile() ? '200px' : `calc(100vw / 24 * 3.5)`;
      mapContainer.value.style.minHeight = isMobile() ? '200px' : `calc(100vw / 24 * 3.5)`;
    }

    // 确保容器有最小尺寸
    if (mapContainer.value.offsetWidth < 200) {
      mapContainer.value.style.minWidth = '200px';
    }
    const minHeight = isMobile() ? 200 : `calc(100vw / 24 * 3.5)`;
    if (mapContainer.value.offsetHeight < (isMobile() ? 200 : 300)) {
      mapContainer.value.style.minHeight = minHeight;
    }

    map.value = new gmaps.Map(mapContainer.value, mapOptions);

    // 触发地图重新计算尺寸
    setTimeout(() => {
      gmaps.event.trigger(map.value, 'resize');
    }, 50);

    // 确保地图完全加载后设置中心点
    gmaps.event.addListenerOnce(map.value, 'idle', () => {
      // 强制设置中心点，确保经纬度在地图正中心
      const centerPoint = new gmaps.LatLng(props.lat, props.lng);
      map.value.setCenter(centerPoint);
      map.value.setZoom(props.zoom);

      // 再次触发resize确保地图正确显示
      setTimeout(() => {
        gmaps.event.trigger(map.value, 'resize');
        map.value.setCenter(centerPoint);
      }, 100);
    });

    // Add marker at the center location (same as map center)
    const markerOptions = {
      position: { lat: props.lat, lng: props.lng }, // 标记位置与地图中心相同
      map: map.value,
      title: props.markerTitle,
    };

    // 如果有自定义图标，添加到标记选项中
    if (props.markerIcon) {
      if (typeof props.markerIcon === 'string') {
        // 如果是SVG字符串，转换为Data URL（参考FindRetailer.vue的实现）
        if (props.markerIcon.includes('<svg') || props.markerIcon.includes('path d=')) {
          markerOptions.icon = {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(props.markerIcon),
            scaledSize: new gmaps.Size(44, 44),
            anchor: new gmaps.Point(22, 22),
          };
        } else {
          // 如果是URL，直接使用
          markerOptions.icon = props.markerIcon;
        }
      } else if (typeof props.markerIcon === 'object') {
        // 如果是对象，直接使用（支持Google Maps的图标配置对象）
        markerOptions.icon = props.markerIcon;
      }
    }

    const marker = new gmaps.Marker(markerOptions);
    markers.value.push(marker);

    // 创建自定义标签覆盖层（参考FindRetailer.vue的实现）
    class CustomLabel extends gmaps.OverlayView {
      constructor(position, text, map) {
        super();
        this.position = position;
        this.text = text;
        this.map = map;
        this.div = null;
        this.setMap(map);
      }

      onAdd() {
        this.div = document.createElement('div');
        this.div.className = 'map-custom-marker-label';
        this.div.innerHTML = this.text;
        this.div.style.position = 'absolute';
        this.div.style.background = 'rgba(255, 255, 255, 0.95)';
        this.div.style.padding = '6px 10px';
        this.div.style.fontFamily = 'Arial, sans-serif';
        this.div.style.fontSize = '14px';
        this.div.style.color = '#4964A2';
        this.div.style.fontWeight = 'bold';
        this.div.style.lineHeight = '1.4';
        this.div.style.whiteSpace = 'pre-line';
        this.div.style.textAlign = 'left';
        this.div.style.maxWidth = '220px';
        this.div.style.wordWrap = 'break-word';
        this.div.style.wordBreak = 'break-word';
        this.div.style.pointerEvents = 'none';
        this.div.style.zIndex = '1000';

        const panes = this.getPanes();
        panes.overlayLayer.appendChild(this.div);
      }

      draw() {
        const overlayProjection = this.getProjection();
        const pixelPosition = overlayProjection.fromLatLngToDivPixel(this.position);

        if (this.div) {
          this.div.style.left = pixelPosition.x + 30 + 'px'; // 向右偏移30px
          this.div.style.top = pixelPosition.y - this.div.offsetHeight / 2 - 10 + 'px'; // 垂直居中并向上偏移10px
        }
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
      }
    }

    // 创建自定义标签（位置与地图中心相同）
    const customLabel = new CustomLabel(
      { lat: props.lat, lng: props.lng }, // 标签位置与地图中心相同
      props.markerTitle,
      map.value
    );
    labels.value.push(customLabel);
  } catch (err) {
    console.error('Error details:', {
      message: err.message,
      stack: err.stack,
      mapContainer: mapContainer.value,
      googleMaps: !!window.google?.maps,
      props: props,
    });
  }
};

// Watch for prop changes
watch(
  () => [props.lat, props.lng, props.markerIcon, props.markerTitle],
  async () => {
    if (map.value && mapContainer.value && window.google && window.google.maps) {
      const gmaps = window.google.maps;
      const newCenter = { lat: props.lat, lng: props.lng };

      // 更新地图中心点
      map.value.setCenter(newCenter);

      // Clear existing markers and labels
      markers.value.forEach((marker) => marker.setMap(null));
      labels.value.forEach((label) => label.onRemove());
      markers.value = [];
      labels.value = [];

      // 在地图中心添加新的标记
      const markerOptions = {
        position: newCenter, // 标记位置与地图中心相同
        map: map.value,
        title: props.markerTitle,
      };

      // 如果有自定义图标，添加到标记选项中
      if (props.markerIcon) {
        if (typeof props.markerIcon === 'string') {
          // 如果是SVG字符串，转换为Data URL（参考FindRetailer.vue的实现）
          if (props.markerIcon.includes('<svg') || props.markerIcon.includes('path d=')) {
            markerOptions.icon = {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(props.markerIcon),
              scaledSize: new gmaps.Size(44, 44),
              anchor: new gmaps.Point(22, 22),
            };
          } else {
            // 如果是URL，直接使用
            markerOptions.icon = props.markerIcon;
          }
        } else if (typeof props.markerIcon === 'object') {
          // 如果是对象，直接使用（支持Google Maps的图标配置对象）
          markerOptions.icon = props.markerIcon;
        }
      }

      const marker = new gmaps.Marker(markerOptions);
      markers.value.push(marker);

      // 创建自定义标签覆盖层
      class CustomLabel extends gmaps.OverlayView {
        constructor(position, text, map) {
          super();
          this.position = position;
          this.text = text;
          this.map = map;
          this.div = null;
          this.setMap(map);
        }

        onAdd() {
          this.div = document.createElement('div');
          this.div.className = 'map-custom-marker-label';
          this.div.innerHTML = this.text;
          this.div.style.position = 'absolute';
          this.div.style.background = 'rgba(255, 255, 255, 0.95)';
          this.div.style.padding = '6px 10px';
          this.div.style.fontFamily = 'Arial, sans-serif';
          this.div.style.fontSize = '14px';
          this.div.style.color = '#4964A2';
          this.div.style.fontWeight = 'bold';
          this.div.style.lineHeight = '1.4';
          this.div.style.whiteSpace = 'pre-line';
          this.div.style.textAlign = 'left';
          this.div.style.maxWidth = '220px';
          this.div.style.wordWrap = 'break-word';
          this.div.style.wordBreak = 'break-word';
          this.div.style.pointerEvents = 'none';
          this.div.style.zIndex = '1000';

          const panes = this.getPanes();
          panes.overlayLayer.appendChild(this.div);
        }

        draw() {
          const overlayProjection = this.getProjection();
          const pixelPosition = overlayProjection.fromLatLngToDivPixel(this.position);

          if (this.div) {
            this.div.style.left = pixelPosition.x + 30 + 'px';
            this.div.style.top = pixelPosition.y - this.div.offsetHeight / 2 - 10 + 'px';
          }
        }

        onRemove() {
          if (this.div && this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }
      }

      // 创建自定义标签（位置与地图中心相同）
      const customLabel = new CustomLabel(
        newCenter, // 标签位置与地图中心相同
        props.markerTitle,
        map.value
      );
      labels.value.push(customLabel);
    }
  },
  { flush: 'post' }
);

watch(
  () => props.zoom,
  (newZoom) => {
    if (map.value && mapContainer.value && newZoom) {
      map.value.setZoom(newZoom);
    }
  },
  { flush: 'post' }
);

// Lifecycle hooks
onMounted(async () => {
  // 等待多个 nextTick 确保 DOM 完全渲染
  await nextTick();
  await nextTick();

  // 确保容器有尺寸后再初始化地图
  const checkContainerSize = () => {
    if (
      mapContainer.value &&
      mapContainer.value.offsetWidth > 0 &&
      mapContainer.value.offsetHeight > 0
    ) {
      initMap();
    } else {
      setTimeout(checkContainerSize, 50);
    }
  };

  checkContainerSize();
});

onUnmounted(() => {
  // 清理标记和标签
  markers.value.forEach((marker) => marker.setMap(null));
  labels.value.forEach((label) => label.onRemove());
  markers.value = [];
  labels.value = [];

  if (map.value) {
    // 清理地图实例
    map.value = null;
  }
  if (mapContainer.value) {
    // 清理容器引用
    mapContainer.value = null;
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.s-map {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 200px; /* 移动端最小高度 */
  position: relative;
}

/* PC端地图高度 */
@media (min-width: 768px) {
  .map-container {
    min-height: calc(100vw / 24 * 3.5);
  }
}

// 自定义地图标记标签样式（仅用于地图组件）
:global(.map-custom-marker-label) {
  background: rgba(255, 255, 255, 0.95) !important;
  padding: 6px 10px !important;
  font-size: 14px !important;
  color: #4964a2 !important;
  font-weight: bold !important;
  line-height: 1.4 !important;
  white-space: pre-line !important;
  text-align: left !important;
  max-width: 140px !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  margin-left: 0px !important;
  transform: translateY(0%) !important;
}
</style>
